import type { ActivityType } from 'types';
import {
  clearRuleValues,
  ruleValueFields,
  validateRuleValues,
  type RuleValueSide,
} from 'modules/agreement/discountTree/form';
import { copyFactorPolicy } from 'modules/agreement/factorPolicy/form';
import { clientTargetInvalidReason } from './clientTarget';
import type { ClientDiscountOwner } from './owner';
import type { ClientAgreementDiscountRule, ClientDiscountContext, ClientRuleFormValues } from './types';

type RuleErrors = Partial<Record<keyof ClientRuleFormValues, string>>;

export const contextFields = {
  standard_discount: { client: 'standard_client_discount', provider: 'standard_provider_discount' },
  non_standard_discount: { client: 'non_standard_client_discount', provider: 'non_standard_provider_discount' },
  package_discount: { client: 'package_client_discount', provider: 'package_provider_discount' },
} as const satisfies Record<ClientDiscountContext, Record<'client' | 'provider', keyof ClientRuleFormValues>>;

export const ruleValueSides: readonly RuleValueSide[] = (['client', 'provider'] as const).map((side) => ({
  side,
  discountField: (context) => contextFields[context][side],
  policyField: `${side}_factor_policy`,
}));

/** The blank draft used by both add and reset flows. */
export function emptyRuleValues(): ClientRuleFormValues {
  return {
    target_path: null,
    target_activity: null,
    target_category: null,
    standard_client_discount: null,
    standard_provider_discount: null,
    non_standard_client_discount: null,
    non_standard_provider_discount: null,
    package_client_discount: null,
    package_provider_discount: null,
    client_factor_policy: null,
    provider_factor_policy: null,
    terms: '',
  };
}

/** Make a draft from a saved rule without mutating the transport object. */
export function formValuesFromRule(rule: ClientAgreementDiscountRule): ClientRuleFormValues {
  const { id: _id, order: _order, ...values } = rule;
  return {
    ...values,
    client_factor_policy: copyFactorPolicy(rule.client_factor_policy),
    provider_factor_policy: copyFactorPolicy(rule.provider_factor_policy),
  };
}

/** Clear both sides of every context that the activity, target, or type cannot match. */
export function clearIncompatibleValues(
  values: ClientRuleFormValues,
  type: ActivityType,
  ownerKind: ClientDiscountOwner['kind'],
): ClientRuleFormValues {
  return clearRuleValues(
    values,
    ruleValueFields(type, values.target_activity, ruleValueSides, ownerKind === 'vendor' ? 'MEDIA' : 'SERVICE'),
  );
}

/** Validate applicability and require a discount or policy on either side. */
export function validateRule(
  values: ClientRuleFormValues,
  type: ActivityType,
  owner: Pick<ClientDiscountOwner, 'kind' | 'id'>,
  allowEmptyRoot = false,
): RuleErrors {
  const fields = ruleValueFields(
    type,
    values.target_activity,
    ruleValueSides,
    owner.kind === 'vendor' ? 'MEDIA' : 'SERVICE',
  );
  const emptyRoot =
    allowEmptyRoot &&
    values.target_path?.length === 1 &&
    !values.target_activity &&
    !values.target_category &&
    !values.terms;
  const errors: RuleErrors = validateRuleValues(values, fields, emptyRoot);
  if (!values.target_path) errors.target_path = 'Select a discount target.';
  else if (clientTargetInvalidReason(values.target_path, type)) {
    errors.target_path = `This target does not match ${type}. Select a compatible target.`;
  } else if (values.target_path[0].kind !== owner.kind || values.target_path[0].id !== owner.id) {
    errors.target_path = 'Select a target within this section.';
  }
  if (values.target_activity && !fields.some((field) => field.context && field.applicable)) {
    errors.target_activity = 'Select an activity compatible with this target.';
  }

  return errors;
}
